import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {Subscription}                                                    from 'rxjs';
import {ApGridPagerType, IGridData, IGridPagerData, IWizardChoiceData}   from '../../ap-interface';
import {ApPermissions}                                                   from 'invoker-transport';
import {
  ApDynComponentComponent
}                                                                        from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig
}                                                                        from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {CropTypeStore}                                                   from '../../stores/base-data/crop.types.store';
import {
  AccessPermissionsStore
}                                                                        from '../../stores/settings/access.permission.store';
import {VarietyStore}                                                    from '../../stores/base-data/variety.store';
import {filter, map}                                                     from 'rxjs/operators';
import {FarmUsersStore}                                                  from '../../stores/farm/farm.users.store';
import {MapViewStore}                                                    from '../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                        from '../../stores/translation/translation.store';
import {
  GetPermissionPipe
}                                                                        from '../../ap-permission/pipes/get-permission.pipe';
import {SettingsStore}                                                   from '../../stores/base-data/settings.store';
import {GetRoundNumericPipe}                                             from '../../ap-utils';
import {ObjectFactory}                                                   from 'ts-tooling';
import IVarieties = Data.BaseData.IVarieties;

enum VarietyGridActions {
  ACTIVE = 1,
  INACTIVE = 0,
}

interface IVarietyGridData extends IGridData<IVarieties> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
  CropType: string;
}

/**
 * Component for the varieties
 */
@Component({
  selector: 'ap-varieties-overview',
  template: `
    <ap-dyncomponent [caption]="'Settings__Lbl_Menu_Varieties'"
                     [canSearch]="true"
                     [headerIcon]="'ap-basedata-icon'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="varietyLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`
})

export class ApVarietiesOverviewComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  columns: ApDynGridColumnConfigBase[] = [];
  pager: ApDynGridPagerConfigBase;
  public varietyLoading$ = this.varietyStore.Listen(s => s.loading);
  items$ = this.varietyStore.Listen(s => s.data).pipe(
    filter(d => !!d),
    map(pp => pp.Convert<IVarietyGridData>((variety: IVarieties) => {
      const cropType = this.croptypeStore.Listen(s => s.data).getValue().Find(c => c?.CropKey === variety?.CroptypesCropkey)?.Description;
      return {
        Id: variety.Id,
        SourceItem: variety,
        Status: variety.Selectable ? 1 : -1,
        StatusClass: variety.Selectable ? 'ap-status-green' : 'ap-status-red',
        StatusDesc: variety.Selectable ? 'Global__Active' : 'Global__Inactive',
        CropType: cropType,
      };
    })));

  private _subscriptions: Array<Subscription> = [];
  private _listMassEditing = [];
  private _applyClicked = new EventEmitter<any>();

  constructor(private varietyStore: VarietyStore,
              private farmUsersStore: FarmUsersStore,
              private translationStore: TranslationStore,
              private croptypeStore: CropTypeStore,
              private mapViewStore: MapViewStore,
              private permissionsStore: AccessPermissionsStore,
              private settingsStore: SettingsStore,
              private roundNumericPipe: GetRoundNumericPipe,
              private permissionPipe: GetPermissionPipe) {
    this.mapViewStore.hideMapView();
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
  }

  ngAfterContentInit(): void {
    this.gridBuilder();
    this.fillWizardChoiceData();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  fillWizardChoiceData(): void {
    const state = {
      id: 0,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Status'),
      type: ApGridPagerType.DropDown,
      values: [
        {
          id: VarietyGridActions.ACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Active'),
        },
        {
          id: VarietyGridActions.INACTIVE,
          label: this.translationStore.FindTranslationForSelectedLanguage('Global__Inactive'),
        }
      ]
    } as IWizardChoiceData;
    this._listMassEditing.push(state);
  }

  private gridBuilder(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        showHeaderTitle: true,
        class: 'StatusClass',
        description: 'StatusDesc',
        filterable: true,
        groupIndex: 0,
        groupDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'CropType',
        title: 'Global__CropType',
        filterable: true,
        width: 150,
        headerFilterable: true,
        sortIndex: 0,
        sortDesc: false,
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Prodname',
        title: 'Global__Description',
        filterable: true,
        width: 100,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Flattenedrisk',
        title: 'Global__Lodging',
        filterable: true,
        width: 80,
        cssClass: 'right', filterType: FilterType.NUMBER,
        pipes: [
          {
            pipe: this.roundNumericPipe,
            args: [
              this.settingsStore.FirstSetting.DigitsAfterDecimalPoint,
            ]
          }
        ],
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Tkg',
        title: 'Global__Thousand_Seed_Weight',
        filterable: true,
        width: 80,
        cssClass: 'right', filterType: FilterType.NUMBER,
        pipes: [
          {
            pipe: this.roundNumericPipe,
            args: [
              this.settingsStore.FirstSetting.DigitsAfterDecimalPoint,
            ]
          }
        ],
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.IdentificationNumber',
        title: 'Docu_Ini__Id',
        filterable: true,
        width: 80,
        cssClass: 'right', filterType: FilterType.NUMBER,
        headerFilterable: true,
        hide: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.YearOfPermission',
        title: 'Global__Admission',
        filterable: true,
        width: 80,
        cssClass: 'right', filterType: FilterType.NUMBER,
        headerFilterable: true,
        hide: true
      }),
      new ApDynGridEditColumnConfig(
        {
          disabled: true
        }
      )
    ];
    this.permissionPipe.transform(ApPermissions.EDIT_VARIETIES).subscribe(permission => {
      this.pager = new ApDynGridPagerConfig({
        listMassEditing: this._listMassEditing,
        apply: this._applyClicked,
        hasPermission: permission,
      });
    });
  }

  private _onApplyClicked(data: IGridPagerData): void {
    const rows = this.dynComponent.getSelectedItems();
    if (data.object.id === 0) {
      {
        rows.forEach(row => {
          const item = ObjectFactory.Copy(row['SourceItem'] as IVarieties);
          if (item) {
            let val = false;
            if (data.value.id === VarietyGridActions.ACTIVE) {
              val = true;
            }
            item.Selectable = val;
            this.varietyStore.updateSelectableVariety(row as IVarieties);
          }
        });
      }
    }
  }
}
